import styled from "@emotion/styled";
import theme from "../../styles/theme";

export const HeroAdvancedBase = styled.div`
  position: relative;
  width: 100%;
  overflow-x: clip;
  margin-bottom: ${theme.spacing[8]};

  &:after {
    content: "";
    display: block;
    width: 100%;
    height: 600px;
    position: absolute;
    bottom: -600px;
    left: 0;
    right: 0;
    background-color: ${theme.colors.white};
  }

  & + div,
  & + div + div {
    position: relative;
  }
`;

export const HeroParallax = styled.div`
  background: linear-gradient(
    to bottom,
    ${theme.colors.blues[10]} 0%,
    ${theme.colors.white} 100%
  );
  border-radius: ${theme.borderRadius};
  margin: 0 ${theme.spacing[4]};
  min-height: 600px;
  max-width: 100%;
  overflow: clip;

  ${theme.largerThan.md} {
    min-height: 600px;
  }

  ${theme.largerThan.lg} {
    min-height: 900px;
    max-width: 1440px;
  }

  margin: 0 auto;

  position: relative;
  z-index: 0;
  display: grid;
  grid-template-areas: "stack";

  > * {
    grid-area: stack;
    animation: parallax linear;
    animation-timeline: scroll();
  }

  @keyframes parallax {
    to {
      transform: translateY(calc(var(--parallax-speed) * 50px));
    }
  }

  @keyframes parallax-clouds {
    to {
      transform: translateX(calc(var(--parallax-cloud) * 100px));
    }
  }

  .sun,
  .cloud1,
  .cloud2,
  .cloud3,
  .cloud4,
  .cloud5 {
    position: absolute;
    top: 0;
    left: 0;
  }

  .sun {
    --parallax-speed: 5;
    top: 0%;
    left: 70%;
    transform: scale(0.5);

    ${theme.largerThan.md} {
      top: 10%;
    }

    ${theme.largerThan.lg} {
      transform: scale(1);
    }
  }

  .cloud1,
  .cloud2,
  .cloud3,
  .cloud4,
  .cloud5 {
    animation: parallax-clouds linear;
    animation-timeline: scroll();
  }

  .cloud1 {
    --parallax-cloud: 10;
    left: 60%;
    top: 5%;
    transform: scale(0.5);

    ${theme.largerThan.md} {
      left: 65%;
      top: 14%;
    }

    ${theme.largerThan.lg} {
      transform: scale(1);
    }
  }

  .cloud2 {
    --parallax-cloud: 5;
    left: 0%;
    top: 60%;
    transform: scale(0.3);

    ${theme.largerThan.md} {
      left: 10%;
      top: 40%;
    }

    ${theme.largerThan.lg} {
      left: 8%;
      top: 37%;
      transform: scale(0.7);
    }
  }

  .cloud3 {
    --parallax-cloud: 3;
    left: 80%;
    top: 35%;
    transform: scale(0.5);

    ${theme.largerThan.lg} {
      top: 30%;
      transform: scale(0.8);
    }
  }

  .cloud4 {
    --parallax-cloud: 7;
    left: 60%;
    top: 55%;
    transform: scale(0.5);

    ${theme.largerThan.lg} {
      top: 40%;
      left: 56%;
      transform: scale(1);
    }
  }

  .cloud5 {
    --parallax-cloud: 2;
    left: 35%;
    top: 65%;
    transform: scale(0.3);

    ${theme.largerThan.md} {
      top: 50%;
      transform: scale(0.5);
    }
  }

  .background {
    position: absolute;
    bottom: 5%;
    left: 0;
    right: 0;
    --parallax-speed: 5;
    z-index: 8;
    align-self: end;
    width: 100%;
    height: auto;
    opacity: 0.8;

    ${theme.largerThan.md} {
    }
  }

  .middleground,
  .foreground {
    align-self: end;
  }

  .middleground {
    position: absolute;
    bottom: -10%;
    left: 0;
    right: 0;
    z-index: 9;
    --parallax-speed: -5;
    width: 100%;
    height: auto;

    ${theme.largerThan.md} {
      bottom: -15%;
    }
  }

  .foreground {
    position: absolute;
    bottom: -20%;
    right: 0;
    width: 100%;
    height: auto;
    justify-self: end;

    --parallax-speed: -15;
    z-index: 10;

    ${theme.largerThan.md} {
      bottom: -25%;
      width: 80%;
    }
  }
`;

export const HeroAdvancedContainer = styled.div`
  max-width: ${theme.maxWidth};
  margin: 0 auto;
  z-index: 5;

  display: grid;
  grid-template-areas: "stack";

  > * {
    grid-area: stack;
    animation: parallax linear;
    animation-timeline: scroll();
  }

  ul {
    display: none !important;
    list-style: none;
    padding: 0;
    margin: 0;

    position: absolute;
    top: 30%;
    left: 0;
    right: 0;
    bottom: 0;

    display: grid;
    grid-template-areas: "stack";

    > * {
      grid-area: stack;
      animation: parallax linear;
      animation-timeline: scroll();
    }

    li {
      width: 300px;
      position: absolute;
      top: 0;
      left: 0;
      --parallax-speed: -10;

      & + li {
        left: 33%;
      }

      & + li + li {
        left: 66%;
      }

      a {
        color: ${theme.colors.blues[100]};
        font-weight: ${theme.fontWeights.semibold};
      }
    }
  }
`;

export const HeroAdvancedContent = styled.div`
  padding: ${theme.spacing[8]} ${theme.spacing[4]};
  width: 80%;
  --parallax-speed: 0;

  ${theme.largerThan.md} {
    width: 60%;
  }

  ${theme.largerThan.xl} {
    padding: ${theme.spacing[8]} 0;
  }

  p {
    font-size: ${theme.fontSizes[2]};
    line-height: ${theme.leading.normal};
    color: ${theme.colors.darkBlues[75]};

    ${theme.largerThan.lg} {
      font-size: ${theme.fontSizes[3]};
    }
  }
`;

export const Backgrounds = styled.div`
  display: grid;
  grid-template-areas: "stack";

  position: absolute;
  bottom: 0;

  background-color: red;

  @keyframes parallax {
    to {
      transform: translateY(calc(var(--parallax-speed) * 500px));
    }
  }

  > * {
    grid-area: stack;
    animation: parallax linear;
    animation-timeline: scroll();
  }

  .background {
  }

  .middleground,
  .foreground {
    position: absolute;
    bottom: 0;
  }

  .foreground {
    --parallax-speed: 100;
  }
`;
